import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-1 — Hook agregador da Visão.
// Faz N fetches em paralelo, expõe loading/error individuais por chunk
// pra cada card poder mostrar skeleton independente. Refresh manual via
// `refetch()` + auto-refresh 30s.
//
// FIN-1.1 (security): TODOS os fetches agora enviam Authorization
// Bearer ${token} — backend exige JWT. user_id em query param é
// preservado por back-compat (backend ignora).

const REFRESH_INTERVAL_MS = 30_000;

async function _safeFetch(url, token) {
  try {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(url, { headers });
    if (!r.ok) return { error: `HTTP ${r.status}`, data: null };
    const data = await r.json();
    return { error: null, data };
  } catch (e) {
    return { error: String(e.message || e), data: null };
  }
}

export default function useFinanceVisao() {
  const { user, token } = useAuth();
  const userId = user?.id;
  const lastFetchedRef = useRef(0);

  const [data, setData] = useState({
    totalBalance: null,
    summary: null,
    recent: null,
    cashflow: null,
    cumulative: null,
    heatmap: null,
  });
  const [loading, setLoading] = useState({
    totalBalance: true,
    summary: true,
    recent: true,
    cashflow: true,
    cumulative: true,
    heatmap: true,
  });
  const [errors, setErrors] = useState({});

  const fetchAll = useCallback(async () => {
    if (!userId || !token) return;
    lastFetchedRef.current = Date.now();
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    // Disparados em paralelo. JWT via Bearer token (FIN-1.1) — backend
    // ignora user_id no query string e usa o do JWT.
    const [totalBalance, summary, recent, cashflow, cumulative, heatmap] = await Promise.all([
      _safeFetch(`${API_URL}/api/accounts/total-balance`, token),
      _safeFetch(`${API_URL}/api/finance/summary?period=month`, token),
      _safeFetch(`${API_URL}/api/finance/transactions?limit=5`, token),
      _safeFetch(`${API_URL}/api/finance/monthly-cashflow?months=6`, token),
      _safeFetch(`${API_URL}/api/finance/cumulative-spending?period=current_month`, token),
      _safeFetch(`${API_URL}/api/finance/daily-heatmap?month=${ym}`, token),
    ]);

    setData({
      totalBalance: totalBalance.data,
      summary: summary.data,
      recent: recent.data,
      cashflow: cashflow.data,
      cumulative: cumulative.data,
      heatmap: heatmap.data,
    });
    setErrors({
      totalBalance: totalBalance.error,
      summary: summary.error,
      recent: recent.error,
      cashflow: cashflow.error,
      cumulative: cumulative.error,
      heatmap: heatmap.error,
    });
    setLoading({
      totalBalance: false, summary: false, recent: false,
      cashflow: false, cumulative: false, heatmap: false,
    });
  }, [userId]);

  // Refetch específico de day-details (drill-down do heatmap).
  const fetchDayDetails = useCallback(async (date) => {
    if (!token || !date) return null;
    const r = await _safeFetch(
      `${API_URL}/api/finance/day-details?date=${date}`,
      token,
    );
    return r.data;
  }, [token]);

  useEffect(() => {
    if (!userId || !token) return;
    fetchAll();
    const interval = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, token, fetchAll]);

  return {
    ...data,
    loading,
    errors,
    refetch: fetchAll,
    fetchDayDetails,
  };
}
